import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, withLatestFrom, filter } from 'rxjs/operators';
import * as fromComm from 'src/app/modules/communications/store/communications';
import { ConfiguredChannel } from 'src/app/modules/communications/store/communications/communications.model';
import { cloneDeep } from 'lodash-es';
import { GUID_EMPTY } from '@app/core/constants';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'topo-slack-edit',
  templateUrl: './slack-edit.component.html',
  styleUrls: ['./slack-edit.component.scss'],
  standalone: false
})
export class SlackEditComponent implements OnInit, OnDestroy {
  @Input() isActive: boolean;
  configuredChannel: ConfiguredChannel;
  selectedChannelId: string;
  sendDirectMessages: boolean;
  destroy$ = new Subject<void>();
  emptyGuid = GUID_EMPTY;
  slackChannels = [];

  constructor(
    private store: Store<fromComm.State>,
    private translate: TranslateService,
    private spinnerService: NgxSpinnerService
  ) {}

  ngOnInit(): void {
    this.store
      .select(fromComm.selectSlackEditIsActive)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isActive => (this.isActive = isActive));

    this.store
      .select(fromComm.selectIsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoading => {
        if (isLoading) {
          this.spinnerService.show();
        } else {
          this.spinnerService.hide();
        }
      });

    this.store
      .select(fromComm.selectSelectedConfiguredChannel)
      .pipe(
        withLatestFrom(this.store.select(fromComm.selectSlackEditIsActive)),
        filter(([selectedChannel, isActive]) => isActive),
        takeUntil(this.destroy$)
      )
      .subscribe(([selectedChannel, isActive]) => {
        this.configuredChannel = !!selectedChannel ? cloneDeep(selectedChannel) : null;
        this.selectedChannelId = this.configuredChannel?.settings?.selectedChannelId ?? '-';
        this.sendDirectMessages = this.configuredChannel?.settings?.sendDirectMessages === true;

        if (this.configuredChannel?.settings?.isConnected && isActive) {
          this.store.dispatch(fromComm.startGetSlackChannels());

          setTimeout(
            () => this.store.dispatch(fromComm.getSlackChannelsForConfiguredChannelFromApi({ channel: { ...this.configuredChannel } })),
            100
          );
        }
      });

    this.store
      .select(fromComm.selectSlackChannelsForConfiguredChannel)
      .pipe(takeUntil(this.destroy$))
      .subscribe(slackChannels => {
        slackChannels = slackChannels
          .map(sc => {
            return {
              ...sc,
              icon: sc.is_private ? 'lock' : 'tag'
            };
          })
          .sort((c1, c2) => (c1.name > c2.name ? 1 : -1));

        this.slackChannels = slackChannels ?? [];
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.store.dispatch(fromComm.closeConfiguredChannelEdit());
  }

  cancelClick(): void {
    this.close();
  }

  saveClick(): void {
    this.store.dispatch(fromComm.saveConfiguredChannelToApi({ channel: { ...this.configuredChannel } }));
  }

  connectToSlack(): void {
    this.store.dispatch(fromComm.saveAndConnectToSlack({ channel: { ...this.configuredChannel } }));
  }

  selectSlackChannel(): void {
    const channel = this.slackChannels.find(c => c.id === this.selectedChannelId);

    const updateChannel = {
      ...this.configuredChannel,
      settings: {
        ...this.configuredChannel?.settings,
        selectedChannelId: channel.id,
        selectedChannelIcon: channel.is_channel ? 'tag' : 'lock',
        selectedChannelName: channel.name
      }
    };

    this.configuredChannel = updateChannel;
  }

  checkDirectMessage(): void {
    this.sendDirectMessages = !this.sendDirectMessages;

    const updateChannel = {
      ...this.configuredChannel,
      settings: {
        ...this.configuredChannel?.settings,
        sendDirectMessages: this.sendDirectMessages
      }
    };

    this.configuredChannel = updateChannel;
  }

  disconnectFromSlack(): void {
    this.store.dispatch(fromComm.disconnectFromSlackWithApi({ channel: { ...this.configuredChannel } }));
  }
}
